import { injectContentsquareScript } from "@contentsquare/tag-sdk";
import { StyledEngineProvider } from "@mui/material/styles";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { setWorkerUrl } from "maplibre-gl";
import maplibreWorkerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import "@/main.css";
import "@/styles/tokens.css";
import "@/styles/typography.css";
import "./i18n";
import { OnlineProvider } from "./providers/OnlineProvider";

import { CssBaseline } from "@/components/atoms/CssBaseline";
import { TooltipV2Provider } from "@/components/atoms/Tooltip";
import { ErrorBoundary } from "@/components/organisms/ErrorBoundary";
import { configureContentsquareCapture } from "@/config/contentsquarePrivacy";
import { initMixpanel } from "@/config/mixpanel";
import {
  FeatureFlagsProvider,
  NotificationProvider,
  ThemeProvider,
} from "@/providers";
import { AuthProvider } from "@/providers/auth";
import { PlantStatusProvider } from "@/providers/plantStatus/PlantStatusProvider";
import { queryClient } from "@/queryClient";
import { router } from "@/routes/routeTree";
import { theme } from "@/styles/theme/theme";

// maplibre-gl v6 ya no embebe el worker como Blob inline: lo sirve como fichero
// aparte y, tras el bundling, import.meta.url no lo resuelve. Hay que darle la URL
// del worker a mano antes de crear el primer Map, o el mapa queda sin teselas en
// producción (CI en verde igualmente). El sufijo ?worker&url arrastra el
// maplibre-gl-shared.mjs hermano, que ?url solo no incluye.
setWorkerUrl(maplibreWorkerUrl);

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Tracing
    tracesSampleRate: 0.1,
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.MODE,
  });
}

if (import.meta.env.VITE_CONTENTSQUARE_CLIENT_ID) {
  configureContentsquareCapture();
  injectContentsquareScript({
    clientId: import.meta.env.VITE_CONTENTSQUARE_CLIENT_ID,
    async: true,
  });
}

initMixpanel();

/**
 * Configuración de MSW (Mock Service Worker) para desarrollo
 *
 * MSW solo se activa cuando se cumplen TODAS estas condiciones:
 * 1. import.meta.env.DEV === true (dev server, no build)
 * 2. import.meta.env.MODE !== "prod" (evita activarse en "npm run dev:prod")
 * 3. import.meta.env.VITE_USE_MOCK_API === "true" (habilitación explícita)
 *
 * El dynamic import permite tree-shaking: MSW se elimina completamente del bundle de producción
 *
 * Escenarios:
 * - npm run dev                           → MSW OFF (falta VITE_USE_MOCK_API)
 * - VITE_USE_MOCK_API=true npm run dev    → MSW ON ✓
 * - npm run dev:prod                      → MSW OFF (MODE === "prod")
 * - npm run build                         → MSW eliminado del bundle
 */
if (
  import.meta.env.DEV &&
  import.meta.env.MODE !== "prod" &&
  import.meta.env.VITE_USE_MOCK_API === "true" &&
  import.meta.env.VITE_E2E_TESTING !== "true"
) {
  const { worker } = await import("@/msw/browser");
  await worker.start();
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <AuthProvider>
        <OnlineProvider>
          <NotificationProvider>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <QueryClientProvider client={queryClient}>
                  <FeatureFlagsProvider>
                    <PlantStatusProvider>
                      {/*
                        Provider de Radix Tooltip a nivel de app: no renderiza
                        DOM (es un Context.Provider, no-op visual), solo aporta
                        el contexto de delay compartido a todos los TooltipV2
                        descendientes. Se monta aquí para no repetirlo en cada
                        tabla/celda que use tooltips.
                      */}
                      <TooltipV2Provider>
                        <RouterProvider router={router} />
                      </TooltipV2Provider>
                    </PlantStatusProvider>
                  </FeatureFlagsProvider>
                </QueryClientProvider>
              </ThemeProvider>
            </StyledEngineProvider>
          </NotificationProvider>
        </OnlineProvider>
      </AuthProvider>
    </ErrorBoundary>
  </StrictMode>,
);
